import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Building2, Crown, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ConsumerLayout from '@/components/ConsumerLayout';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function PricingPage() {
  const { isAuthenticated, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => { api.get('/monetization/plans').then(r => setPlans(r.data.plans || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) { toast.info('Sign in first'); return; }
    setSubscribing(planId);
    try {
      const res = await api.post('/monetization/plans/subscribe', { plan_id: planId, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else toast.success(`${planId} plan activated!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubscribing(null); }
  };

  const PLAN_ICONS = { starter: Zap, creator_pro: Crown, venue_agency: Building2 };
  const PLAN_COLORS = { starter: '#10b981', creator_pro: '#6366f1', venue_agency: '#D4AF37' };

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-indigo-600/20 text-indigo-300 border-indigo-500/30">Pricing</Badge>
          <h1 className="text-4xl font-bold text-white mb-3">Plans for Every Creator</h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">No upfront costs. Start free, upgrade as you grow. You only pay when you earn.</p>
        </div>

        {loading ? <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map(plan => {
              const Icon = PLAN_ICONS[plan.id] || Zap;
              const color = PLAN_COLORS[plan.id] || '#6366f1';
              const isPopular = plan.popular;
              const isCurrent = user?.plan === plan.id;
              
              return (
                <Card key={plan.id} className={`bg-slate-800/50 border-slate-700 relative overflow-hidden ${isPopular ? 'border-indigo-500 ring-2 ring-indigo-500/20' : ''}`}>
                  {isPopular && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 to-purple-600" />}
                  {isPopular && <Badge className="absolute top-3 right-3 bg-indigo-600 text-white text-[10px]">Most Popular</Badge>}
                  <CardHeader className="text-center pb-2">
                    <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${color}22` }}>
                      <Icon className="w-7 h-7" style={{ color }} />
                    </div>
                    <CardTitle className="text-white text-xl">{plan.name}</CardTitle>
                    <p className="text-slate-400 text-sm">{plan.description}</p>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">${plan.price_monthly}</span>
                      <span className="text-slate-500">/mo</span>
                    </div>
                    <ul className="text-left space-y-3 mb-8">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full h-12 font-semibold" style={{ backgroundColor: isCurrent ? 'transparent' : color, color: isCurrent ? color : 'white', border: isCurrent ? `2px solid ${color}` : 'none' }}
                      disabled={isCurrent || subscribing === plan.id}
                      onClick={() => handleSubscribe(plan.id)}>
                      {subscribing === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? 'Current Plan' : plan.price_monthly === 0 ? 'Start Free' : <>Get Started <ArrowRight className="w-4 h-4 ml-1" /></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="text-center mt-12 text-slate-500 text-sm">
          <p>All plans include Stripe-powered payments, SSL security, and EVENT★GOERS branding.</p>
          <p className="mt-1">Questions? <Link to="/partner/register" className="text-indigo-400 hover:underline">Contact us</Link></p>
        </div>
      </div>
    </ConsumerLayout>
  );
}
